import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import api, { apiErr, fmtTime, fmtTimeLong } from "../../lib/api";
import { Card, SectionTitle, Alert, btnSecondary } from "../ui-bits";
import { toast } from "sonner";
import {
  Trash2, CheckCircle2, Film, Image as ImageIcon, MousePointerClick, GripVertical, Sparkles, BellRing,
  User, MapPin, Map, CalendarDays, BarChart3, TrendingUp, Newspaper, Megaphone, Quote, Archive,
  History, IdCard, Zap, Stamp, BookOpen, ListChecks, Columns2, Sun, Grid3x3,
  ListOrdered, Type, Plane, Globe2, LineChart, PenLine,
} from "lucide-react";
import MyFolder from "../broll/MyFolder";

// Mismo orden y fórmula que TRANSITION_STYLE_NAMES/apply_overlays en el backend
// (backend/highlights.py, backend/server.py): el estilo del corte i es
// TRANSITION_STYLE_NAMES[i % 4], determinista — nunca aleatorio, así que el frontend puede
// PREDECIR qué estilo tocó a cada corte sin pedírselo al backend (no hay endpoint para esto,
// es pura función del índice de la escena). Si el orden/número de estilos cambia en Python,
// hay que actualizar esta lista a la vez o el timeline mostrará el estilo equivocado.
const TRANSITION_STYLE_NAMES = ["flash", "cover", "lightleak", "grid"];
const TRANSITION_STYLE_META = {
  flash: { Icon: Zap, color: "text-amber-500" },
  cover: { Icon: Columns2, color: "text-rose-500" },
  lightleak: { Icon: Sun, color: "text-orange-500" },
  grid: { Icon: Grid3x3, color: "text-indigo-500" },
};

// Un icono/color por tipo de resalte (antes todos menos "cta" compartían el mismo Sparkles
// fucsia, indistinguibles entre sí en el timeline). Tipos reales: HYPERFRAMES_TIPOS en
// backend/highlights.py — mantener esta lista sincronizada cada vez que se añada un tipo ahí.
// El fallback (tipo desconocido/legacy) usa Sparkles fucsia.
const HIGHLIGHT_STYLE = {
  nombre: { Icon: User, color: "text-fuchsia-600", chip: "text-fuchsia-400" },
  lugar: { Icon: MapPin, color: "text-emerald-600", chip: "text-emerald-400" },
  fecha: { Icon: CalendarDays, color: "text-blue-600", chip: "text-blue-400" },
  dato: { Icon: TrendingUp, color: "text-amber-600", chip: "text-amber-400" },
  titular: { Icon: Megaphone, color: "text-orange-600", chip: "text-orange-400" },
  cta: { Icon: BellRing, color: "text-red-600", chip: "text-red-400" },
  cita: { Icon: Quote, color: "text-sky-600", chip: "text-sky-400" },
  archivo: { Icon: Archive, color: "text-stone-600", chip: "text-stone-400" },
  prensa: { Icon: Newspaper, color: "text-slate-600", chip: "text-slate-400" },
  estadistica: { Icon: BarChart3, color: "text-indigo-600", chip: "text-indigo-400" },
  mapa: { Icon: Map, color: "text-teal-600", chip: "text-teal-400" },
  cronologia: { Icon: History, color: "text-cyan-600", chip: "text-cyan-400" },
  ficha: { Icon: IdCard, color: "text-pink-600", chip: "text-pink-400" },
  revelacion: { Icon: Zap, color: "text-yellow-600", chip: "text-yellow-400" },
  sello: { Icon: Stamp, color: "text-rose-600", chip: "text-rose-400" },
  capitulo: { Icon: BookOpen, color: "text-purple-600", chip: "text-purple-400" },
  lista: { Icon: ListChecks, color: "text-lime-600", chip: "text-lime-400" },
  ranking: { Icon: ListOrdered, color: "text-lime-700", chip: "text-lime-500" },
  texto_cinetico: { Icon: Type, color: "text-fuchsia-500", chip: "text-fuchsia-300" },
  trayecto: { Icon: Plane, color: "text-teal-500", chip: "text-teal-300" },
  mapa_datos: { Icon: Globe2, color: "text-teal-700", chip: "text-teal-500" },
  tendencia: { Icon: LineChart, color: "text-amber-700", chip: "text-amber-500" },
  anotacion: { Icon: PenLine, color: "text-orange-500", chip: "text-orange-300" },
};
const highlightStyle = (tipo) => HIGHLIGHT_STYLE[tipo] || { Icon: Sparkles, color: "text-fuchsia-600", chip: "text-fuchsia-400" };

export default function TabBroll({ project, refreshProject, setTab }) {
  const { t } = useTranslation();
  const segments = project?.segments || [];
  const [clips, setClips] = useState(project?.broll?.clips || []);
  const [activeSceneId, setActiveSceneId] = useState(segments[0]?.id || null);
  const [library, setLibrary] = useState({ videos: [], folders: [] });
  const [loadingLib, setLoadingLib] = useState(false);
  const [dragInfo, setDragInfo] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);
  const [highlights, setHighlights] = useState(project?.highlights_preview || {});

  const selectScene = (id, el) => {
    setActiveSceneId(id);
    requestAnimationFrame(() => {
      const header = document.querySelector("header");
      const offset = (header?.offsetHeight || 0) + 12;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    });
  };

  const loadHighlights = async () => {
    try { const { data } = await api.get(`/projects/${project.id}/highlights`); setHighlights(data.highlights || {}); } catch (e) { /* noop */ }
  };

  const loadLibrary = async () => {
    setLoadingLib(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/broll/library`);
      setLibrary(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoadingLib(false); }
  };

  useEffect(() => { if (project?.id && segments.length) { loadLibrary(); loadHighlights(); } }, [project?.id]); // eslint-disable-line

  if (!segments.length) {
    return <Alert type="warning" testid="broll-no-segments">{t("brl.noSegments")}</Alert>;
  }

  const sceneAudio = (s) => s.narration_duration || s.duration || 0;
  const sceneClips = (sid) => clips.filter((c) => c.segment_id === sid);
  const sceneCov = (s) => {
    const au = sceneAudio(s);
    const a = sceneClips(s.id).reduce((x, c) => x + (c.duration || 0), 0);
    return { au, a, pct: Math.round(Math.min(100, au ? (a / au) * 100 : 100)) };
  };
  const totalAudio = segments.reduce((x, s) => x + sceneAudio(s), 0);
  const overall = totalAudio
    ? Math.round(segments.reduce((acc, s) => { const { au, a } = sceneCov(s); return acc + Math.min(a, au); }, 0) / totalAudio * 100)
    : 0;
  const allDone = segments.every((s) => sceneCov(s).pct >= 100);

  // Marcadores (resaltes ✨ y CTA 🔔) en tiempo ABSOLUTO sobre el vídeo completo.
  const videoMarkers = (() => {
    let offset = 0;
    const out = [];
    for (const s of segments) {
      (highlights[s.id] || []).forEach((h) => out.push({ ...h, abs: offset + (h.start || 0), sceneNum: s.num }));
      offset += sceneAudio(s);
    }
    return out;
  })();

  // Cortes entre escenas (destello que tapa el corte, ver apply_overlays en highlights.py) en
  // tiempo ABSOLUTO — uno por cada frontera interna (no hay corte antes de la primera escena
  // ni después de la última). El estilo se predice con la misma fórmula determinista del backend.
  const cutMarkers = (() => {
    let offset = 0;
    const out = [];
    for (let i = 0; i < segments.length; i++) {
      offset += sceneAudio(segments[i]);
      if (i < segments.length - 1) {
        out.push({ abs: offset, style: TRANSITION_STYLE_NAMES[i % TRANSITION_STYLE_NAMES.length] });
      }
    }
    return out;
  })();

  const activeScene = segments.find((s) => s.id === activeSceneId) || segments[0];
  const sceneLabel = (s) => t("brl.sceneTitle", { num: s.num, title: s.title });

  const save = async (next) => {
    const { data } = await api.put(`/projects/${project.id}/broll`, { clips: next });
    setClips(data.clips); refreshProject();
  };

  const remove = (id) => { save(clips.filter((c) => c.id !== id)).catch((e) => toast.error(apiErr(e))); };

  const reorder = (sid, from, to) => {
    if (from === to || from == null || to == null) return;
    const scl = clips.filter((c) => c.segment_id === sid);
    if (to < 0 || to >= scl.length) return;
    const moved = [...scl];
    const [item] = moved.splice(from, 1);
    moved.splice(to, 0, item);
    let idx = 0;
    const next = clips.map((c) => (c.segment_id === sid ? moved[idx++] : c));
    save(next).then(() => toast.success(t("brl.orderUpdated"))).catch((e) => toast.error(apiErr(e)));
  };

  const clipKey = (c) => `${c.video_url || c.thumbnail || ""}|${c.title || ""}|${c.start ?? ""}|${c.end ?? ""}|${c.media || ""}`;

  // Igual que el relleno de una escena, pero recorre TODAS las escenas incompletas en una sola
  // pasada: reparte los clips frescos de la carpeta sin repetir entre escenas mientras alcancen,
  // y si se agotan, repite (previa confirmación única, no una por escena) para completar el resto.
  const fillAllScenes = async (newClips) => {
    const incomplete = segments.filter((s) => sceneCov(s).pct < 100);
    if (!incomplete.length) { toast.info(t("brl.allScenesAlreadyDone")); return; }
    const allPool = newClips.filter((c) => (c.duration || 0) > 0);
    if (allPool.length === 0) { toast.warning(t("brl.noDurationClips")); return; }
    if (!window.confirm(t("brl.fillAllConfirm", { count: incomplete.length }))) return;

    const used = new Set(clips.map(clipKey));
    const additions = [];
    let repeatedAny = false;
    for (const s of incomplete) {
      const { au, a } = sceneCov(s);
      let remaining = au - a;
      if (remaining <= 0) continue;
      let pool = allPool.filter((c) => !used.has(clipKey(c))).sort(() => Math.random() - 0.5);
      if (pool.length === 0) {
        repeatedAny = true;
        pool = [...allPool].sort(() => Math.random() - 0.5);
      }
      for (let i = 0; i < pool.length && remaining > 0; i++) {
        const c = pool[i];
        additions.push({ ...c, id: crypto.randomUUID(), segment_id: s.id });
        used.add(clipKey(c));
        remaining -= (c.duration || 0);
      }
    }
    if (!additions.length) { toast.info(t("brl.allScenesAlreadyDone")); return; }
    try {
      await save([...clips, ...additions]);
      toast.success(t(repeatedAny ? "brl.allFilledWithRepeats" : "brl.allFilled", { count: additions.length, scenes: incomplete.length }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const fillWith = async (newClips, label, opts = {}) => {
    if (opts.fill && opts.fillAll) { await fillAllScenes(newClips); return; }
    if (!activeScene) { toast.warning(t("brl.chooseSceneFirst")); return; }
    if (opts.fill) {
      const { au, a } = sceneCov(activeScene);
      let remaining = au - a;
      if (remaining <= 0) { toast.info(t("brl.sceneComplete")); return; }
      const allPool = newClips.filter((c) => (c.duration || 0) > 0);
      if (allPool.length === 0) { toast.warning(t("brl.noDurationClips")); return; }
      const used = new Set(clips.map(clipKey));
      const fresh = [...allPool.filter((c) => !used.has(clipKey(c)))].sort(() => Math.random() - 0.5);

      // CASO A: no quedan clips sin usar -> preguntar si repetir (una ronda más, sin ciclar hasta llenar)
      if (fresh.length === 0) {
        if (!window.confirm(t("brl.repeatConfirm"))) return;
        const shuffledAll = [...allPool].sort(() => Math.random() - 0.5);
        const repeats = [];
        for (let j = 0; j < shuffledAll.length && remaining > 0; j++) {
          repeats.push({ ...shuffledAll[j], id: crypto.randomUUID(), segment_id: activeScene.id });
          remaining -= (shuffledAll[j].duration || 0);
        }
        try {
          await save([...clips, ...repeats]);
          toast.success(t("brl.repeatedAdded", { count: repeats.length, num: activeScene.num }));
        } catch (e) { toast.error(apiErr(e)); }
        return;
      }

      // CASO B: hay clips sin usar -> añadirlos (sin preguntar, aunque no llene la escena)
      const freshToAdd = [];
      for (let i = 0; i < fresh.length && remaining > 0; i++) {
        freshToAdd.push(fresh[i]);
        remaining -= (fresh[i].duration || 0);
      }
      try {
        const withSeg = freshToAdd.map((c) => ({ ...c, id: crypto.randomUUID(), segment_id: activeScene.id }));
        await save([...clips, ...withSeg]);
        if (remaining > 0) {
          toast.success(t("brl.freshNotFull", { count: freshToAdd.length, time: fmtTimeLong(remaining) }));
        } else {
          toast.success(t("brl.sceneCompleted", { num: activeScene.num, count: freshToAdd.length }));
        }
      } catch (e) { toast.error(apiErr(e)); }
      return;
    }
    // Añadir clip(s) manualmente (permite repetir si el usuario lo elige)
    try {
      const withSeg = newClips.map((c) => ({ ...c, id: crypto.randomUUID(), segment_id: activeScene.id }));
      await save([...clips, ...withSeg]);
      toast.success(t("brl.clipsAdded", { count: newClips.length, label, num: activeScene.num }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <SectionTitle sub={t("brl.subtitle")}>{t("brl.title")}</SectionTitle>

      {/* Cobertura global */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-lg font-bold text-slate-900">{t("brl.totalCoverage")}</span>
          <span data-testid="coverage-pct" className={`text-3xl font-bold font-head ${overall >= 100 ? "text-emerald-600" : "text-amber-600"}`}>{overall}%</span>
        </div>
        <div className="relative w-full h-7 bg-slate-200 rounded-full overflow-visible">
          <div className="w-full h-full rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ${overall >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${overall}%` }} />
          </div>
          {totalAudio > 0 && cutMarkers.map((c, i) => {
            const left = Math.min(99.6, Math.max(0, (c.abs / totalAudio) * 100));
            const { Icon, color } = TRANSITION_STYLE_META[c.style];
            return (
              <div key={i} className="group/cut absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 cursor-default"
                style={{ left: `${left}%` }} data-testid={`cut-marker-${i}`}>
                <div className="w-0.5 h-9 bg-white/80 rounded-full shadow" />
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/cut:block z-30 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 shadow-xl">
                  <div className={`flex items-center gap-1.5 text-white text-xs font-bold`}>
                    <Icon size={12} className={color} />
                    {t(`brl.transitionStyle.${c.style}`, { defaultValue: c.style })}
                  </div>
                  <div className="text-slate-400 text-[11px] mt-0.5">{t("brl.transitionAt", { time: fmtTime(c.abs) })}</div>
                  <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
                </div>
              </div>
            );
          })}
        </div>

        {videoMarkers.length > 0 && totalAudio > 0 && (
          <div className="relative h-6 mt-1.5" data-testid="video-timeline-markers">
            <div className="absolute inset-x-0 top-1/2 h-px bg-slate-200" />
            {videoMarkers.map((h, i) => {
              const left = Math.min(99.2, Math.max(0, (h.abs / totalAudio) * 100));
              const isCta = h.tipo === "cta";
              const label = isCta ? t("brl.subscribeCard") : h.texto;
              const { Icon, color, chip } = highlightStyle(h.tipo);
              return (
                <div key={i} className="group/vm absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
                  style={{ left: `${left}%` }}>
                  <Icon size={15} className={`${color} bg-white rounded-full cursor-default`} />
                  <div className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover/vm:block z-30 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 shadow-xl">
                    <div className={`text-[10px] font-bold uppercase tracking-wide ${chip}`}>{t(`brl.tipo.${h.tipo}`, { defaultValue: h.tipo })}</div>
                    <div className="flex items-center gap-1.5 text-white text-xs font-bold mt-0.5">
                      <Icon size={12} className={chip} />
                      {label}
                    </div>
                    <div className="text-slate-400 text-[11px] mt-0.5">{t("brl.scene")} {h.sceneNum} · {fmtTime(h.abs)}</div>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-slate-900" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-3 text-base text-slate-600">
          {allDone ? t("brl.allDone") : t("brl.completeEach")}
        </div>
        {allDone && <button data-testid="continue-composition-button" onClick={() => setTab("composicion")} className={`w-full mt-4 ${btnSecondary}`}>{t("brl.continueComp")}</button>}

        {Object.keys(highlights).length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-600 flex flex-col gap-1">
            <span className="flex items-center gap-1.5"><Sparkles size={16} className="text-fuchsia-600" /> <span><Trans i18nKey="brl.highlightsInfo1" components={{ 0: <b /> }} /></span></span>
            <span className="flex items-center gap-1.5"><BellRing size={16} className="text-red-600" /> <span><Trans i18nKey="brl.highlightsInfo2" components={{ 0: <b /> }} /></span></span>
          </div>
        )}
      </Card>

      {/* Lista de escenas */}
      <div className="space-y-4" data-testid="scenes-list">
        {segments.map((s) => {
          const { au, pct } = sceneCov(s);
          const sc = sceneClips(s.id);
          const active = activeScene?.id === s.id;
          const done = pct >= 100;
          return (
            <div key={s.id} className="space-y-4">
            <Card onClick={(e) => selectScene(s.id, e.currentTarget)} data-testid={`scene-card-${s.id}`} className={`p-5 transition-colors cursor-pointer ${active ? "border-[#2563EB] ring-2 ring-[#2563EB]/20" : "hover:border-slate-300"}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  {done ? <CheckCircle2 size={24} className="text-emerald-600 shrink-0" /> : <span className="w-6 h-6 rounded-full border-2 border-slate-300 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-lg font-bold text-slate-900 truncate">{t("brl.sceneTitle", { num: s.num, title: s.title })}</div>
                    <div className="text-sm text-slate-500">{t("brl.needs", { time: fmtTimeLong(au) })}</div>
                    {(s.keywords?.length > 0 || s.personajes?.length > 0) && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {(s.keywords || []).map((k) => <span key={`k-${k}`} className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded">{k}</span>)}
                        {(s.personajes || []).map((p) => <span key={`p-${p}`} className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1">👤 {p}</span>)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xl font-bold font-head ${done ? "text-emerald-600" : "text-amber-600"}`}>{pct}%</span>
                  <button
                    data-testid={`select-scene-${s.id}`}
                    onClick={() => setActiveSceneId(s.id)}
                    className={`min-h-[44px] px-4 rounded-lg text-sm font-bold flex items-center gap-1.5 whitespace-nowrap shrink-0 transition-colors ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                    {active ? <><CheckCircle2 size={16} className="shrink-0" /> {t("brl.activeScene")}</> : <><MousePointerClick size={16} className="shrink-0" /> {t("brl.chooseMaterial")}</>}
                  </button>
                </div>
              </div>
              <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden mb-3">
                <div className={`h-full rounded-full transition-all duration-300 ${done ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
              </div>
              {(() => {
                const totalDur = sc.reduce((x, c) => x + (c.duration || 0), 0);
                const base = Math.max(au || 0, totalDur, 1);
                const fillerPct = au > totalDur ? ((au - totalDur) / base) * 100 : 0;
                return (
                  <>
                  {sc.length >= 2 && (
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5">
                      <GripVertical size={14} className="text-slate-400" />
                      {t("brl.dragHint")}
                    </div>
                  )}
                  <div className="flex w-full gap-0.5 bg-slate-50 rounded-lg p-2 min-h-[64px]" data-testid={`scene-clips-${s.id}`}>
                    {sc.length === 0 && <span className="text-sm text-slate-400 px-2 self-center">{t("brl.noClips")} {active ? t("brl.addFromFolder") : t("brl.pressChoose")}</span>}
                    {sc.map((c, i) => {
                      const dragging = dragInfo?.sid === s.id && dragInfo?.idx === i;
                      const dropping = dragInfo?.sid === s.id && dropIdx === i && !dragging;
                      return (
                      <div
                        key={c.id}
                        data-testid={`scene-clip-${c.id}`}
                        draggable
                        onDragStart={() => setDragInfo({ sid: s.id, idx: i })}
                        onDragEnd={() => { setDragInfo(null); setDropIdx(null); }}
                        onDragOver={(e) => { e.preventDefault(); if (dragInfo?.sid === s.id) setDropIdx(i); }}
                        onDrop={(e) => { e.preventDefault(); if (dragInfo?.sid === s.id) reorder(s.id, dragInfo.idx, i); setDragInfo(null); setDropIdx(null); }}
                        title={`${c.title} · ${c.duration}s`}
                        className={`relative h-12 rounded overflow-hidden flex items-end group bg-blue-600 cursor-grab active:cursor-grabbing transition-all ${dragging ? "opacity-40" : ""} ${dropping ? "ring-2 ring-amber-400" : ""}`}
                        style={{ width: `${((c.duration || 0) / base) * 100}%`, minWidth: 26 }}>
                        {c.thumbnail && (
                          <img src={c.thumbnail} alt="" loading="lazy" decoding="async"
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
                        {c.media === "image" ? <ImageIcon size={12} className="absolute top-0.5 left-0.5 z-10 text-white/90" /> : <Film size={12} className="absolute top-0.5 left-0.5 z-10 text-white/90" />}
                        <span className="relative z-10 text-xs text-white truncate px-1 pb-0.5 font-medium drop-shadow">{fmtTime(c.duration || 0)}</span>
                        {sc.length >= 2 && (
                          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-20 flex justify-between px-0.5 opacity-0 group-hover:opacity-100">
                            <button data-testid={`move-left-${c.id}`} disabled={i === 0} onClick={() => reorder(s.id, i, i - 1)} className="bg-black/70 text-white rounded-full p-0.5 disabled:opacity-0 hover:bg-black"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M15 18l-6-6 6-6"/></svg></button>
                            <button data-testid={`move-right-${c.id}`} disabled={i === sc.length - 1} onClick={() => reorder(s.id, i, i + 1)} className="bg-black/70 text-white rounded-full p-0.5 disabled:opacity-0 hover:bg-black"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M9 18l6-6-6-6"/></svg></button>
                          </div>
                        )}
                        <button data-testid={`remove-clip-${c.id}`} onClick={() => remove(c.id)} className="absolute top-0.5 right-0.5 z-20 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
                      </div>
                      );
                    })}
                    {fillerPct > 0 && sc.length > 0 && (
                      <div title={t("brl.missingFull", { time: fmtTimeLong(au - totalDur) })} className="relative h-12 rounded flex items-center justify-center border-2 border-dashed border-amber-300 bg-amber-50/50" style={{ width: `${fillerPct}%`, minWidth: 18 }}>
                        <span className="text-[11px] text-amber-600 font-semibold truncate px-1">{t("brl.missing", { time: fmtTimeLong(au - totalDur) })}</span>
                      </div>
                    )}
                  </div>
                  {(highlights[s.id] || []).length > 0 && (
                    <div className="mt-2" data-testid={`scene-highlights-${s.id}`}>
                      <div className="relative h-6 w-full">
                        <div className="absolute inset-x-0 top-1/2 h-px bg-fuchsia-200" />
                        {(highlights[s.id] || []).map((h, hi) => {
                          const left = Math.min(99, Math.max(0, (h.start / base) * 100));
                          const isCta = h.tipo === "cta";
                          const label = isCta ? t("brl.subscribeCard") : h.texto;
                          const { Icon, color, chip } = highlightStyle(h.tipo);
                          return (
                            <div key={hi} data-testid={`highlight-marker-${s.id}-${hi}`}
                              className="group/hl absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
                              style={{ left: `${left}%` }}>
                              <Icon size={16} className={`${color} bg-white rounded-full cursor-default`} />
                              <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/hl:block z-30 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 shadow-xl">
                                <div className={`text-[10px] font-bold uppercase tracking-wide ${chip}`}>{t(`brl.tipo.${h.tipo}`, { defaultValue: h.tipo })}</div>
                                <div className="flex items-center gap-1.5 text-white text-xs font-bold mt-0.5">
                                  <Icon size={12} className={chip} />
                                  {label}
                                </div>
                                {!isCta && h.subtitulo && <div className="text-slate-300 text-[11px] mt-0.5">{h.subtitulo}</div>}
                                <div className="text-slate-400 text-[11px] mt-0.5">{t("brl.appearsAt", { time: fmtTime(h.start) })}</div>
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </>
                );
              })()}
            </Card>
            {active && (
              <div className="sm:ml-4 sm:pl-5 sm:border-l-4 sm:border-blue-300">
                <MyFolder projectId={project.id} keywords={s.keywords || []} characters={s.personajes || []} library={library} loading={loadingLib} onReload={loadLibrary} onUse={fillWith}
                  targetLabel={sceneLabel(s)} emptyScene={sc.length === 0} />
              </div>
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
